// Colectas (ver informes "LYNX — Colectas: diseño ajustado" y "LYNX —
// Colectas WebApp: interfaz unificada y confirmaciones diarias"): una sola
// tabla que combina la plantilla habitual con el recorrido diario de la
// fecha operativa elegida. Reusa los mismos endpoints de
// /v2/route-templates y /v2/routes: no hay una segunda entidad de datos,
// solo una pantalla distinta sobre lo mismo. Colectas fijas
// (src/pages/ColectasFijasPage.tsx) sigue existiendo sin cambios.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type Store = { id: number; nombre_fantasia: string; direccion?: string | null; codigo_postal?: string | number | null };
type Driver = { id: number; username: string; first_name: string | null; last_name: string | null };
type Departure = {
  id?: number; departure_time: string; weekdays: number[]; window_seq: number;
  default_driver_id: number | null; default_driver_username?: string | null;
  default_driver_first_name?: string | null; default_driver_last_name?: string | null; active: boolean;
};
type TemplateStop = { sequence: number; store_id: number; pickup_time: string | null; store_name: string; store_address?: string | null; postal_code?: string | null };
type Template = { id: number; name: string; notes: string | null; active: boolean; stops: TemplateStop[]; departures: Departure[] };
type GeneratedRoute = { id: number; status: string; scheduled_date: string; driver_id: number | null; driver_username: string | null; template_id: number | null; template_name: string | null; departure_time: string | null; stops_count: number; notes: string | null; confirmation_state: "ACCEPTED" | "DECLINED" | "PENDING" | "OVERDUE" | null };

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
// Misma convención que Colectas fijas y que Tiendas: L-V/Sábado/Domingo como
// grupo. weekdays sigue siendo un array libre; esto es sólo cómo se ofrece
// completarlo y cómo se filtra por "metodología" en el listado.
const DAY_GROUPS: { key: string; label: string; days: readonly number[] }[] = [
  { key: "weekdays", label: "Lun-Vie", days: [1, 2, 3, 4, 5] },
  { key: "saturday", label: "Sáb", days: [6] },
  { key: "sunday", label: "Dom", days: [0] },
];
const hhmm = (t: string | null | undefined) => (t ? t.slice(0, 5) : "—");
const driverName = (d: { username: string; first_name?: string | null; last_name?: string | null } | null | undefined) =>
  d ? [d.first_name, d.last_name].filter(Boolean).join(" ") || d.username : "—";
const isoPlus = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

// Estados pedidos: Sin asignar / Pendiente / Confirmada / Rechazada.
// OVERDUE (vencida) se muestra como Pendiente con una aclaración, no es un
// quinto estado — así no se contradice el listado de estados del diseño.
const CONFIRMATION: Record<string, { label: string; color: string }> = {
  ACCEPTED: { label: "Confirmada", color: "var(--success)" },
  DECLINED: { label: "Rechazada", color: "var(--danger)" },
  PENDING: { label: "Pendiente", color: "var(--warning, #d29922)" },
  OVERDUE: { label: "Pendiente · vencida", color: "var(--danger)" },
};

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const selectStyle: React.CSSProperties = { width: "100%", minHeight: "48px", padding: "0 14px", borderRadius: "16px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const chip = (on: boolean): React.CSSProperties => ({ padding: "4px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? "var(--primary, #4f8cff)" : "var(--border)"}`, color: on ? "var(--primary, #4f8cff)" : "var(--text-secondary)", background: "transparent" });

function Modal({ title, onClose, children, width = "960px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

// Metodología a partir de weekdays[]: si una salida calza exacto con un
// grupo conocido (L-V/Sáb/Dom) se usa esa etiqueta; si no, se listan los
// días. Varias salidas con metodologías distintas se muestran todas.
function methodologyLabel(departures: Departure[]): string {
  if (!departures.length) return "—";
  const labels = departures.map((d) => {
    const g = DAY_GROUPS.find((g) => g.days.length === d.weekdays.length && g.days.every((x) => d.weekdays.includes(x)));
    return g ? g.label : d.weekdays.map((w) => WEEKDAYS[w]).join("");
  });
  return Array.from(new Set(labels)).join(" + ");
}
function matchesMethodology(departures: Departure[], days: readonly number[]): boolean {
  return departures.some((d) => d.weekdays.length === days.length && days.every((x) => d.weekdays.includes(x)));
}
function habitualDriverSummary(departures: Departure[]): string {
  const names = departures.map((d) => (d.default_driver_id ? driverName({ username: d.default_driver_username ?? "", first_name: d.default_driver_first_name, last_name: d.default_driver_last_name }) : null));
  const present = names.filter(Boolean) as string[];
  if (!present.length) return "—";
  const uniq = Array.from(new Set(present));
  return uniq.length === 1 && present.length === departures.length ? uniq[0] : "varios";
}

export default function ColectasPage() {
  const { token } = useAuth();
  const req = useCallback(async (path: string, init?: RequestInit) => {
    const res = await authFetch(path, { ...(init ?? {}), token, json: init?.body !== undefined });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
    return json;
  }, [token]);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [generated, setGenerated] = useState<GeneratedRoute[]>([]);
  const [genDate, setGenDate] = useState(isoPlus(1));
  const [methodologyFilter, setMethodologyFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  const [editingEventual, setEditingEventual] = useState<number | "new" | null>(null);
  const [bulkPreview, setBulkPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const flash = (m: string) => { setNotice(m); window.setTimeout(() => setNotice(null), 6000); };

  const loadTemplates = useCallback(async () => {
    try { setTemplates((await req("/v2/route-templates?all=1")).templates ?? []); } catch (e: any) { setError(e.message); }
  }, [req]);
  const loadGenerated = useCallback(async () => {
    // Trae habituales Y eventuales de la fecha (ya no se filtra por
    // template_id acá -- cada sección de la pantalla hace su propio recorte).
    try { setGenerated((await req(`/v2/routes?date=${genDate}&type=PICKUP`)).routes ?? []); } catch (e: any) { setError(e.message); }
  }, [req, genDate]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadGenerated(); }, [loadGenerated]);
  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([req("/v2/stores"), req("/v2/routes/drivers")]);
        setStores((s.stores ?? []).map((x: any) => ({ id: Number(x.id), nombre_fantasia: x.nombre_fantasia ?? `Tienda ${x.id}`, direccion: x.direccion, codigo_postal: x.codigo_postal })));
        setDrivers(d.drivers ?? []);
      } catch (e: any) { setError(e.message); }
    })();
  }, [req]);

  const assign = async (routeId: number, driverId: string) => {
    try {
      await req(`/v2/routes/${routeId}/driver`, { method: "PUT", body: JSON.stringify({ driver_id: driverId ? Number(driverId) : null }) });
      flash(driverId ? "Chofer asignado" : "Chofer quitado");
      loadGenerated();
    } catch (e: any) { setError(e.message); }
  };
  const requestOne = async (routeId: number) => {
    try {
      await req(`/v2/routes/${routeId}/request-confirmation`, { method: "POST" });
      flash("Confirmación solicitada");
      loadGenerated();
    } catch (e: any) { setError(e.message); }
  };
  const requestBulk = async () => {
    setBusy(true);
    try {
      const r = await req("/v2/route-templates/request-confirmation-bulk", { method: "POST", body: JSON.stringify({ date: genDate }) });
      setBulkPreview(false);
      flash(`${r.requested.length} colectas pedidas, ${r.notified} choferes avisados${r.withoutDriver.length ? ` · ${r.withoutDriver.length} sin chofer (no se tocaron)` : ""}`);
      loadGenerated();
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };

  const active = templates.filter((t) => t.active), inactive = templates.filter((t) => !t.active);
  const visible = useMemo(() => {
    const all = [...active, ...inactive];
    const group = DAY_GROUPS.find((g) => g.key === methodologyFilter);
    return group ? all.filter((t) => matchesMethodology(t.departures, group.days)) : all;
  }, [active, inactive, methodologyFilter]);
  const routesForTemplate = useCallback((templateId: number) => generated.filter((r) => r.template_id === templateId), [generated]);
  const eventualRoutes = useMemo(() => generated.filter((r) => !r.template_id), [generated]);

  // Preview de la solicitud masiva: se calcula del lado del cliente con lo
  // que ya está cargado para `genDate` -- no hace falta un endpoint aparte
  // solo para mostrar el modal de confirmación.
  const bulkEligible = useMemo(() => generated.filter((r) => r.driver_id && ["ASSIGNED", "LOADED"].includes(r.status)), [generated]);
  const bulkWithoutDriver = useMemo(() => generated.filter((r) => !r.driver_id), [generated]);
  const bulkDriverCount = useMemo(() => new Set(bulkEligible.map((r) => r.driver_id)).size, [bulkEligible]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Colectas</h2>
            <p style={subtitleStyle}>Las colectas habituales: tiendas, horario de retiro y chofer. El estado corresponde a la fecha operativa elegida.</p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button variant="secondary" onClick={() => setBulkPreview(true)} disabled={busy || bulkEligible.length === 0}>Solicitar confirmación masiva</Button>
            <Button onClick={() => setEditing("new")}>Nueva colecta</Button>
          </div>
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error} <Button variant="secondary" onClick={() => setError(null)}>Cerrar</Button></div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "end", marginBottom: "12px" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ ...smallMutedStyle, fontWeight: 600 }}>Metodología</span>
            <button type="button" style={chip(methodologyFilter === null)} onClick={() => setMethodologyFilter(null)}>Todas</button>
            {DAY_GROUPS.map((g) => (
              <button key={g.key} type="button" style={chip(methodologyFilter === g.key)} onClick={() => setMethodologyFilter(g.key)}>{g.label}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "end" }}>
            <Input label="Fecha operativa" type="date" value={genDate} onChange={(e) => setGenDate(e.target.value || isoPlus(1))} />
            <Button variant="secondary" onClick={loadGenerated}>Actualizar</Button>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Colecta</th><th style={thStyle}>Tiendas</th><th style={thStyle}>Metodología</th><th style={thStyle}>Chofer</th><th style={thStyle}>Estado</th><th style={thStyle}>Acciones</th></tr></thead>
            <tbody>
              {visible.length === 0 ? <tr><td style={tdStyle} colSpan={6}><span style={smallMutedStyle}>Todavía no hay colectas. Creá la primera con «Nueva colecta».</span></td></tr> : null}
              {visible.map((t) => {
                const routes = routesForTemplate(t.id);
                return (
                  <tr key={t.id} style={t.active ? undefined : { opacity: 0.55 }}>
                    <td style={tdStyle}><b>{t.name}</b>{!t.active ? <span style={{ ...badge("var(--text-secondary)"), marginLeft: 8 }}>inactiva</span> : null}{t.notes ? <div style={smallMutedStyle}>{t.notes}</div> : null}</td>
                    <td style={tdStyle}>{t.stops.length} <span style={smallMutedStyle}>· {t.stops.slice(0, 4).map((s) => s.store_name).join(", ")}{t.stops.length > 4 ? "…" : ""}</span></td>
                    <td style={tdStyle}>{methodologyLabel(t.departures)}</td>
                    {routes.length === 0 ? (
                      <>
                        <td style={tdStyle}>{habitualDriverSummary(t.departures)} <span style={smallMutedStyle}>(habitual)</span></td>
                        <td style={tdStyle}><span style={smallMutedStyle}>sin recorrido ese día</span></td>
                        <td style={tdStyle}><Button variant="secondary" onClick={() => setEditing(t)}>Editar</Button></td>
                      </>
                    ) : (
                      <>
                        <td style={tdStyle}>
                          {routes.map((r) => (
                            <div key={r.id} style={{ marginBottom: routes.length > 1 ? 6 : 0 }}>
                              {routes.length > 1 ? <span style={smallMutedStyle}>{hhmm(r.departure_time)} · </span> : null}
                              <select style={{ ...selectStyle, minHeight: 36, minWidth: 180, display: "inline-block", width: "auto" }} value={r.driver_id ?? ""} onChange={(e) => assign(r.id, e.target.value)} disabled={!["PLANNED", "ASSIGNED"].includes(r.status)}>
                                <option value="">— sin chofer —</option>
                                {drivers.map((d) => <option key={d.id} value={d.id}>{driverName(d)}</option>)}
                              </select>
                            </div>
                          ))}
                        </td>
                        <td style={tdStyle}>
                          {routes.map((r) => {
                            const label = !r.driver_id ? { label: "Sin asignar", color: "var(--text-secondary)" } : r.confirmation_state ? CONFIRMATION[r.confirmation_state] : null;
                            return <div key={r.id} style={{ marginBottom: routes.length > 1 ? 6 : 0 }}>{label ? <span style={badge(label.color)}>{label.label}</span> : <span style={smallMutedStyle}>—</span>}</div>;
                          })}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                            <Button variant="secondary" onClick={() => setEditing(t)}>Editar</Button>
                            {routes.filter((r) => r.driver_id && ["ASSIGNED", "LOADED"].includes(r.status)).map((r) => (
                              <Button key={r.id} variant="secondary" onClick={() => requestOne(r.id)}>Solicitar confirmación{routes.length > 1 ? ` (${hhmm(r.departure_time)})` : ""}</Button>
                            ))}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "12px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>Colectas eventuales <span style={smallMutedStyle}>· {genDate}</span></h3>
            <p style={{ ...smallMutedStyle, margin: "4px 0 0" }}>Para una fecha puntual, sin recurrencia. No generan instancias futuras ni se convierten en colecta habitual.</p>
          </div>
          <Button onClick={() => setEditingEventual("new")}>Nueva colecta eventual</Button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Colecta</th><th style={thStyle}>Tiendas</th><th style={thStyle}>Chofer</th><th style={thStyle}>Estado</th><th style={thStyle}>Acciones</th></tr></thead>
            <tbody>
              {eventualRoutes.length === 0 ? <tr><td style={tdStyle} colSpan={5}><span style={smallMutedStyle}>No hay colectas eventuales para esta fecha.</span></td></tr> : null}
              {eventualRoutes.map((r) => {
                const label = !r.driver_id ? { label: "Sin asignar", color: "var(--text-secondary)" } : r.confirmation_state ? CONFIRMATION[r.confirmation_state] : null;
                return (
                  <tr key={r.id}>
                    <td style={tdStyle}><b>{r.notes || `Eventual #${r.id}`}</b></td>
                    <td style={tdStyle}>{r.stops_count}</td>
                    <td style={tdStyle}>
                      <select style={{ ...selectStyle, minHeight: 36, minWidth: 180, display: "inline-block", width: "auto" }} value={r.driver_id ?? ""} onChange={(e) => assign(r.id, e.target.value)} disabled={!["PLANNED", "ASSIGNED"].includes(r.status)}>
                        <option value="">— sin chofer —</option>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{driverName(d)}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>{label ? <span style={badge(label.color)}>{label.label}</span> : <span style={smallMutedStyle}>—</span>}</td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                        {["PLANNED", "ASSIGNED"].includes(r.status) ? <Button variant="secondary" onClick={() => setEditingEventual(r.id)}>Editar</Button> : null}
                        {r.driver_id && ["ASSIGNED", "LOADED"].includes(r.status) ? <Button variant="secondary" onClick={() => requestOne(r.id)}>Solicitar confirmación</Button> : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {bulkPreview ? (
        <Modal title="Solicitar confirmación masiva" onClose={() => setBulkPreview(false)} width="520px">
          <p style={{ color: "var(--text-primary)", lineHeight: 1.6 }}>
            Fecha: <b>{genDate}</b><br />
            Colectas a pedir: <b>{bulkEligible.length}</b><br />
            Choferes afectados: <b>{bulkDriverCount}</b>
            {bulkWithoutDriver.length ? <><br /><span style={smallMutedStyle}>{bulkWithoutDriver.length} colecta(s) sin chofer: quedan pendientes de asignación, no se les pide nada.</span></> : null}
          </p>
          <p style={{ ...smallMutedStyle, margin: "8px 0 16px" }}>Los recorridos elegibles pasan a pendiente y se avisa a cada chofer. Las confirmaciones de otros días no cambian.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="secondary" onClick={() => setBulkPreview(false)}>Cancelar</Button>
            <Button onClick={requestBulk} disabled={busy}>{busy ? "Enviando…" : "Confirmar y enviar"}</Button>
          </div>
        </Modal>
      ) : null}

      {editing ? (
        <TemplateModal template={editing === "new" ? null : editing} stores={stores} drivers={drivers} req={req}
          onClose={() => setEditing(null)}
          onSaved={(name) => { setEditing(null); flash(`Colecta «${name}» guardada`); loadTemplates(); loadGenerated(); }} />
      ) : null}

      {editingEventual ? (
        <EventualModal routeId={editingEventual === "new" ? null : editingEventual} date={genDate} stores={stores} drivers={drivers} req={req}
          onClose={() => setEditingEventual(null)}
          onSaved={(creating) => { setEditingEventual(null); flash(creating ? "Colecta eventual creada" : "Colecta eventual actualizada"); loadGenerated(); }} />
      ) : null}
    </div>
  );
}

type EditableStop = { store_id: number; pickup_time: string | null };

// Orden por horario ascendente, estable entre iguales (incluye "sin
// horario", que va al final) -- misma regla que aplica el backend
// (templateService.sortByPickupTime): esto es solo cómo se ve mientras se
// edita, el servidor vuelve a ordenar igual al guardar.
function sortByPickupTime(stops: EditableStop[]): EditableStop[] {
  return stops
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      if (a.s.pickup_time && b.s.pickup_time) return a.s.pickup_time < b.s.pickup_time ? -1 : a.s.pickup_time > b.s.pickup_time ? 1 : a.i - b.i;
      if (a.s.pickup_time && !b.s.pickup_time) return -1;
      if (!a.s.pickup_time && b.s.pickup_time) return 1;
      return a.i - b.i;
    })
    .map(({ s }) => s);
}

// Elegir Stores + horario de retiro, con auto-orden por horario ascendente.
// Compartido entre la plantilla habitual (TemplateModal) y la colecta
// eventual (EventualModal): misma UI, misma regla de orden.
function StopsPicker({ stops, setStops, stores }: { stops: EditableStop[]; setStops: React.Dispatch<React.SetStateAction<EditableStop[]>>; stores: Store[] }) {
  const [filter, setFilter] = useState("");
  const storeById = useMemo(() => new Map(stores.map((s) => [s.id, s])), [stores]);
  const ordered = useMemo(() => sortByPickupTime(stops), [stops]);
  const candidates = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const have = new Set(stops.map((s) => s.store_id));
    return stores.filter((s) => !have.has(s.id) && (!f || s.nombre_fantasia.toLowerCase().includes(f) || String(s.codigo_postal ?? "").includes(f))).slice(0, 40);
  }, [stores, stops, filter]);
  const setPickupTime = (storeId: number, pickup_time: string | null) => setStops((xs) => xs.map((s) => (s.store_id === storeId ? { ...s, pickup_time } : s)));
  const removeStop = (storeId: number) => setStops((xs) => xs.filter((s) => s.store_id !== storeId));

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px", marginBottom: 16 }}>
      <div style={panelStyle}>
        <h4 style={{ margin: "0 0 4px", color: "var(--text-primary)" }}>Tiendas y horario de retiro <span style={smallMutedStyle}>· {stops.length}</span></h4>
        <p style={{ ...smallMutedStyle, margin: "0 0 8px" }}>El orden se calcula solo, por horario ascendente. Sin horario, la tienda queda al final.</p>
        {stops.length === 0 ? <div style={smallMutedStyle}>Agregá tiendas desde la lista de la derecha.</div> : null}
        {ordered.map((s, i) => {
          const st = storeById.get(s.store_id);
          return (
            <div key={s.store_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ ...smallMutedStyle, width: 22 }}>{i + 1}.</span>
              <span style={{ flex: 1, color: "var(--text-primary)" }}>{st?.nombre_fantasia ?? `Tienda ${s.store_id}`} <span style={smallMutedStyle}>{st?.codigo_postal ? `· CP ${st.codigo_postal}` : ""}</span></span>
              <input type="time" value={s.pickup_time ?? ""} onChange={(e) => setPickupTime(s.store_id, e.target.value || null)}
                style={{ padding: "6px 8px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" }} />
              <button type="button" style={chip(false)} onClick={() => removeStop(s.store_id)} aria-label="quitar">×</button>
            </div>
          );
        })}
      </div>
      <div style={panelStyle}>
        <h4 style={{ margin: "0 0 8px", color: "var(--text-primary)" }}>Agregar tienda</h4>
        <Input label="Buscar" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="nombre o CP" />
        <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 8 }}>
          {candidates.map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
              <span style={{ color: "var(--text-primary)" }}>{s.nombre_fantasia} <span style={smallMutedStyle}>{s.codigo_postal ? `· CP ${s.codigo_postal}` : ""}</span></span>
              <button type="button" style={chip(true)} onClick={() => setStops((xs) => [...xs, { store_id: s.id, pickup_time: null }])}>Agregar</button>
            </div>
          ))}
          {candidates.length === 0 ? <div style={smallMutedStyle}>Sin tiendas para ese filtro.</div> : null}
        </div>
      </div>
    </div>
  );
}

// Colecta eventual: fecha puntual, sin plantilla. `routeId` null = creación;
// con id, edita las paradas de una ya creada (mientras siga PLANNED/
// ASSIGNED -- assertEditable del backend corta lo demás). La fecha y el
// nombre (notes) no se pueden editar después de creada: no hay endpoint
// para eso y no corresponde inventarlo para este alcance -- si hace falta
// cambiarlos, se cancela y se crea de nuevo.
function EventualModal({ routeId, date, stores, drivers, req, onClose, onSaved }: {
  routeId: number | null; date: string; stores: Store[]; drivers: Driver[];
  req: (p: string, i?: RequestInit) => Promise<any>; onClose: () => void; onSaved: (creating: boolean) => void;
}) {
  const [loading, setLoading] = useState(!!routeId);
  const [notes, setNotesState] = useState("");
  const [driverId, setDriverId] = useState<number | null>(null);
  const [stops, setStops] = useState<EditableStop[]>([]);
  const [original, setOriginal] = useState<EditableStop[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) return;
    (async () => {
      try {
        const r = await req(`/v2/routes/${routeId}`);
        const route = r.route;
        setNotesState(route.notes ?? "");
        setDriverId(route.driver_id ?? null);
        const loaded = (route.stops ?? []).map((s: any) => ({ store_id: s.store_id, pickup_time: s.pickup_time_snapshot ? String(s.pickup_time_snapshot).slice(0, 5) : null }));
        setStops(loaded);
        setOriginal(loaded);
      } catch (e: any) { setError(e.message); } finally { setLoading(false); }
    })();
  }, [routeId, req]);

  const ordered = useMemo(() => sortByPickupTime(stops), [stops]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      if (!routeId) {
        await req("/v2/routes", {
          method: "POST",
          body: JSON.stringify({
            type: "PICKUP", scheduled_date: date, notes: notes || null,
            driver_id: driverId,
            stops: ordered.map((s) => ({ type: "PICKUP", store_id: s.store_id, pickup_time: s.pickup_time || null })),
          }),
        });
        onSaved(true);
        return;
      }
      // Edición: diff contra lo que había al abrir (por store_id + horario).
      // Quitar y agregar reusa los mismos endpoints que ya existen para
      // cualquier recorrido -- no hace falta un "reemplazar paradas" nuevo
      // para rutas sueltas.
      const key = (s: EditableStop) => `${s.store_id}:${s.pickup_time ?? ""}`;
      const keepKeys = new Set(ordered.map(key));
      const hadKeys = new Set(original.map(key));
      const toRemove = original.filter((s) => !keepKeys.has(key(s)));
      const toAdd = ordered.filter((s) => !hadKeys.has(key(s)));
      if (toRemove.length || toAdd.length) {
        const current = await req(`/v2/routes/${routeId}`);
        const stopIdByKey = new Map(current.route.stops.map((s: any) => [key({ store_id: s.store_id, pickup_time: s.pickup_time_snapshot ? String(s.pickup_time_snapshot).slice(0, 5) : null }), s.id]));
        for (const s of toRemove) {
          const stopId = stopIdByKey.get(key(s));
          if (stopId) await req(`/v2/routes/${routeId}/stops/${stopId}`, { method: "DELETE" });
        }
        for (const s of toAdd) {
          await req(`/v2/routes/${routeId}/stops`, { method: "POST", body: JSON.stringify({ type: "PICKUP", store_id: s.store_id, pickup_time: s.pickup_time || null }) });
        }
        // Reordena para que quede exactamente en el orden por horario que
        // se ve en pantalla (addStop siempre agrega al final).
        const after = await req(`/v2/routes/${routeId}`);
        const idByStore = new Map(after.route.stops.map((s: any) => [s.store_id, s.id]));
        const orderedIds = ordered.map((s) => idByStore.get(s.store_id)).filter(Boolean);
        if (orderedIds.length === after.route.stops.length) {
          await req(`/v2/routes/${routeId}/stops/order`, { method: "PUT", body: JSON.stringify({ stop_ids: orderedIds }) });
        }
      }
      onSaved(false);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title={routeId ? `Colecta eventual #${routeId}` : "Nueva colecta eventual"} onClose={onClose}>
      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginBottom: 12 }}>{error}</div> : null}
      {loading ? <div style={smallMutedStyle}>Cargando…</div> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px", marginBottom: 16 }}>
            <Input label="Fecha operativa" type="date" value={date} disabled />
            <Input label="Nombre / notas" value={notes} onChange={(e) => setNotesState(e.target.value)} placeholder="COLECTA EXTRA — ARROYITO" />
            <label style={{ display: "block" }}>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Chofer</span>
              <select style={{ ...selectStyle, minHeight: 40 }} value={driverId ?? ""} onChange={(e) => setDriverId(e.target.value ? Number(e.target.value) : null)} disabled={!!routeId}>
                <option value="">— se asigna después —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{driverName(d)}</option>)}
              </select>
              {routeId ? <span style={{ ...smallMutedStyle, display: "block", marginTop: 4 }}>El chofer se cambia desde la tabla de colectas eventuales.</span> : null}
            </label>
          </div>
          <StopsPicker stops={stops} setStops={setStops} stores={stores} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} disabled={saving || stops.length === 0}>{saving ? "Guardando…" : routeId ? "Guardar cambios" : "Crear colecta eventual"}</Button>
          </div>
        </>
      )}
    </Modal>
  );
}

function TemplateModal({ template, stores, drivers, req, onClose, onSaved }: { template: Template | null; stores: Store[]; drivers: Driver[]; req: (p: string, i?: RequestInit) => Promise<any>; onClose: () => void; onSaved: (name: string) => void }) {
  const [name, setName] = useState(template?.name ?? "");
  const [notes, setNotes] = useState(template?.notes ?? "");
  const [active, setActive] = useState(template?.active ?? true);
  const [stops, setStops] = useState<EditableStop[]>(template?.stops.map((s) => ({ store_id: s.store_id, pickup_time: s.pickup_time ? s.pickup_time.slice(0, 5) : null })) ?? []);
  const [departures, setDepartures] = useState<Departure[]>(template?.departures.map((d) => ({ ...d })) ?? [{ departure_time: "09:00", weekdays: [1, 2, 3, 4, 5], window_seq: 1, default_driver_id: null, active: true }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ordered = useMemo(() => sortByPickupTime(stops), [stops]);
  const setDep = (i: number, patch: Partial<Departure>) => setDepartures((ds) => ds.map((d, k) => (k === i ? { ...d, ...patch } : d)));
  const toggleDayGroup = (i: number, days: readonly number[]) =>
    setDepartures((ds) => ds.map((d, k) => {
      if (k !== i) return d;
      const allOn = days.every((day) => d.weekdays.includes(day));
      const weekdays = allOn
        ? d.weekdays.filter((w) => !days.includes(w))
        : Array.from(new Set([...d.weekdays, ...days])).sort((a, b) => a - b);
      return { ...d, weekdays };
    }));
  // Un día suelto (lunes y jueves, martes y viernes, etc.): los 3 atajos de
  // arriba no alcanzan para esas combinaciones -- weekdays siempre fue un
  // array libre en el backend, esto solo agrega la forma de armarlo a mano.
  const toggleDay = (i: number, day: number) =>
    setDepartures((ds) => ds.map((d, k) => (k !== i ? d : {
      ...d,
      weekdays: d.weekdays.includes(day) ? d.weekdays.filter((w) => w !== day) : [...d.weekdays, day].sort((a, b) => a - b),
    })));

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = {
        name, notes: notes || null, active,
        stores: ordered.map((s) => ({ store_id: s.store_id, pickup_time: s.pickup_time || null })),
        departures: departures.map((d) => ({ id: d.id, departure_time: d.departure_time, weekdays: d.weekdays, window_seq: d.window_seq, default_driver_id: d.default_driver_id, active: d.active })),
      };
      if (template) await req(`/v2/route-templates/${template.id}`, { method: "PUT", body: JSON.stringify(body) });
      else await req("/v2/route-templates", { method: "POST", body: JSON.stringify(body) });
      onSaved(name);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title={template ? `Colecta «${template.name}»` : "Nueva colecta"} onClose={onClose}>
      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginBottom: 12 }}>{error}</div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px", marginBottom: 16 }}>
        <Input label="Nombre" value={name} onChange={(e) => setName(e.target.value)} placeholder="MAÑANEROS 01" />
        <Input label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="opcional" />
        {template ? <label style={{ display: "flex", alignItems: "center", gap: 8, alignSelf: "end", color: "var(--text-primary)" }}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Colecta activa (se genera)</label> : null}
      </div>

      <StopsPicker stops={stops} setStops={setStops} stores={stores} />

      <div style={{ ...panelStyle, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ margin: 0, color: "var(--text-primary)" }}>Salidas <span style={smallMutedStyle}>· a veces se sale más de una vez por día</span></h4>
          <Button variant="secondary" onClick={() => setDepartures((ds) => [...ds, { departure_time: "17:00", weekdays: [1, 2, 3, 4, 5], window_seq: 2, default_driver_id: null, active: true }])}>Agregar salida</Button>
        </div>
        {departures.map((d, i) => (
          <div key={d.id ?? `n${i}`} style={{ display: "grid", gridTemplateColumns: "110px 1fr 110px 220px 40px", gap: 10, alignItems: "center", padding: "8px 0", borderTop: i ? "1px solid var(--border)" : undefined }}>
            <Input label="Hora" type="time" value={d.departure_time.slice(0, 5)} onChange={(e) => setDep(i, { departure_time: e.target.value })} />
            <div>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Días</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                {DAY_GROUPS.map((g) => (
                  <button key={g.key} type="button" style={chip(g.days.every((day) => d.weekdays.includes(day)))} onClick={() => toggleDayGroup(i, g.days)}>{g.label}</button>
                ))}
              </div>
              {/* Día suelto: para "lunes y jueves", "martes y viernes", etc. -- los
                  atajos de arriba son combinaciones comunes, esto cubre cualquier otra. */}
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {WEEKDAYS.map((label, day) => (
                  <button key={day} type="button" style={{ ...chip(d.weekdays.includes(day)), padding: "3px 8px", fontSize: 11 }} onClick={() => toggleDay(i, day)}>{label}</button>
                ))}
              </div>
            </div>
            <label style={{ display: "block" }}>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Pasada</span>
              <select style={{ ...selectStyle, minHeight: 40 }} value={d.window_seq} onChange={(e) => setDep(i, { window_seq: Number(e.target.value) })}><option value={1}>1ª</option><option value={2}>2ª</option><option value={3}>3ª</option></select>
            </label>
            <label style={{ display: "block" }}>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Chofer habitual</span>
              <select style={{ ...selectStyle, minHeight: 40 }} value={d.default_driver_id ?? ""} onChange={(e) => setDep(i, { default_driver_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">— se asigna cada día —</option>
                {drivers.map((x) => <option key={x.id} value={x.id}>{driverName(x)} · {x.username}</option>)}
              </select>
            </label>
            <button type="button" style={{ ...chip(false), alignSelf: "end" }} onClick={() => setDepartures((ds) => ds.filter((_, k) => k !== i))} aria-label="quitar salida" disabled={departures.length === 1}>×</button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !name.trim() || stops.length === 0}>{saving ? "Guardando…" : "Guardar colecta"}</Button>
      </div>
    </Modal>
  );
}
